import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL //||
  //"https://1maj7uejdd.execute-api.ap-south-1.amazonaws.com/prod_korv_rfm_api_stage";
const AUTH_API_BASE_URL =
  process.env.REACT_APP_AUTH_API_URL ||
  process.env.REACT_APP_API_URL //||
  //"https://1maj7uejdd.execute-api.ap-south-1.amazonaws.com/prod_korv_rfm_api_stage";
const AUTH_TOKEN_KEY = "rfm_auth_token";
const AUTH_ID_TOKEN_KEY = "rfm_auth_id_token";
const AUTH_REFRESH_TOKEN_KEY = "rfm_auth_refresh_token";
const EXPLORE_TRIAL_ID_KEY = "rfm_explore_trial_id";
const EMBEDDED_MODE_KEY = "rfm_embedded_mode";
const EMBEDDED_ORG_ID_KEY = "rfm_embedded_org_id";
const AUTH_EXPIRED_EVENT = "rfm:auth-expired";
const AUTH_SYNC_KEY = "rfm_auth_sync";
const ACCESS_TOKEN_REFRESH_LEEWAY_MS = 60000;
const TRANSIENT_RETRY_STATUSES = new Set([500, 502, 503, 504]);
const TRANSIENT_RETRY_MAX_ATTEMPTS = 2;
const TRANSIENT_RETRY_BASE_DELAY_MS = 500;

const refreshClient = axios.create({
  baseURL: AUTH_API_BASE_URL,
  timeout: 60000,
});

let refreshInFlight = null;
let proactiveRefreshTimer = null;

function normalizeRequestError(error) {
  if (!error) {
    return error;
  }

  const currentMessage = String(error.message || "").trim();
  if (error.code === "ECONNABORTED" || /timeout/i.test(currentMessage)) {
    error.message =
      "The request timed out. This can happen if the network is slow, unavailable, or the server took too long to respond.";
    return error;
  }

  if (!error.response) {
    error.message =
      "The request could not reach the server. Please check your network connection or server availability.";
    return error;
  }

  return error;
}

function isIdempotentRequest(config) {
  const method = String(config?.method || "get").toLowerCase();
  return method === "get" || method === "head" || method === "options";
}

function waitForRetry(delayMs) {
  return new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
}

export function attachTransientRetryInterceptor(client) {
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      normalizeRequestError(error);

      const config = error?.config;
      const status = Number(error?.response?.status || 0);
      if (!config || !TRANSIENT_RETRY_STATUSES.has(status) || !isIdempotentRequest(config)) {
        throw error;
      }

      const currentAttempt = Number(config._transientRetryCount || 0);
      if (currentAttempt >= TRANSIENT_RETRY_MAX_ATTEMPTS) {
        throw error;
      }

      config._transientRetryCount = currentAttempt + 1;
      await waitForRetry(TRANSIENT_RETRY_BASE_DELAY_MS * config._transientRetryCount);
      return client(config);
    }
  );
}

function getAuthStorage() {
  if (typeof window === "undefined") {
    return null;
  }
  if (window.sessionStorage.getItem(EMBEDDED_MODE_KEY) === "1") {
    return window.sessionStorage;
  }
  return window.localStorage;
}

function migrateLegacySessionTokens() {
  if (typeof window !== "undefined" && window.sessionStorage.getItem(EMBEDDED_MODE_KEY) === "1") {
    return;
  }
  const storage = getAuthStorage();
  if (!storage || typeof window === "undefined") {
    return;
  }

  const hasLocalAccess = String(storage.getItem(AUTH_TOKEN_KEY) || "").trim();
  const legacyAccess = String(window.sessionStorage.getItem(AUTH_TOKEN_KEY) || "").trim();
  if (!hasLocalAccess && legacyAccess) {
    storage.setItem(AUTH_TOKEN_KEY, legacyAccess);
  }

  const hasLocalId = String(storage.getItem(AUTH_ID_TOKEN_KEY) || "").trim();
  const legacyId = String(window.sessionStorage.getItem(AUTH_ID_TOKEN_KEY) || "").trim();
  if (!hasLocalId && legacyId) {
    storage.setItem(AUTH_ID_TOKEN_KEY, legacyId);
  }

  const hasLocalRefresh = String(storage.getItem(AUTH_REFRESH_TOKEN_KEY) || "").trim();
  const legacyRefresh = String(window.sessionStorage.getItem(AUTH_REFRESH_TOKEN_KEY) || "").trim();
  if (!hasLocalRefresh && legacyRefresh) {
    storage.setItem(AUTH_REFRESH_TOKEN_KEY, legacyRefresh);
  }

  window.sessionStorage.removeItem(AUTH_TOKEN_KEY);
  window.sessionStorage.removeItem(AUTH_ID_TOKEN_KEY);
  window.sessionStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
}

function dispatchAuthExpired(detail = {}) {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(AUTH_EXPIRED_EVENT, {
      detail,
    })
  );
}

function publishAuthSync(action, detail = {}) {
  if (typeof window === "undefined") {
    return;
  }

  const payload = {
    action,
    detail,
    timestamp: Date.now(),
  };

  try {
    window.localStorage.setItem(AUTH_SYNC_KEY, JSON.stringify(payload));
  } catch {
    // Ignore sync failures and continue local session updates.
  }
}

function clearProactiveRefreshTimer() {
  if (typeof window === "undefined" || proactiveRefreshTimer === null) {
    return;
  }

  window.clearTimeout(proactiveRefreshTimer);
  proactiveRefreshTimer = null;
}

function decodeJwtPayload(token) {
  if (typeof window === "undefined") {
    return null;
  }

  const value = String(token || "").trim();
  if (!value) {
    return null;
  }

  const parts = value.split(".");
  if (parts.length < 2) {
    return null;
  }

  try {
    const normalized = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    return JSON.parse(window.atob(padded));
  } catch {
    return null;
  }
}

function scheduleProactiveRefresh() {
  if (typeof window === "undefined") {
    return;
  }

  clearProactiveRefreshTimer();

  const accessToken = getAuthToken();
  const refreshToken = getAuthRefreshToken();
  if (!accessToken || !refreshToken) {
    return;
  }

  const payload = decodeJwtPayload(accessToken);
  const expiresAtMs = Number(payload?.exp || 0) * 1000;
  if (!Number.isFinite(expiresAtMs) || expiresAtMs <= 0) {
    return;
  }

  const delayMs = Math.max(expiresAtMs - Date.now() - ACCESS_TOKEN_REFRESH_LEEWAY_MS, 0);
  proactiveRefreshTimer = window.setTimeout(async () => {
    try {
      await refreshAuthSession();
    } catch (error) {
      clearAuthToken();
      dispatchAuthExpired({
        reason: "refresh_failed",
        status: Number(error?.response?.status || 0),
        message:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          "Session expired. Please login again.",
      });
    }
  }, delayMs);
}

export function getExploreTrialId() {
  if (typeof window === "undefined") {
    return "";
  }

  const sessionValue = String(window.sessionStorage.getItem(EXPLORE_TRIAL_ID_KEY) || "").trim();
  if (sessionValue) {
    return sessionValue;
  }

  const localValue = String(window.localStorage.getItem(EXPLORE_TRIAL_ID_KEY) || "").trim();
  if (localValue) {
    window.sessionStorage.setItem(EXPLORE_TRIAL_ID_KEY, localValue);
  }
  return localValue;
}

export function setExploreTrialId(exploreTrialId) {
  if (typeof window === "undefined") {
    return;
  }

  const value = String(exploreTrialId || "").trim();
  if (value) {
    window.sessionStorage.setItem(EXPLORE_TRIAL_ID_KEY, value);
    window.localStorage.setItem(EXPLORE_TRIAL_ID_KEY, value);
    return;
  }

  window.sessionStorage.removeItem(EXPLORE_TRIAL_ID_KEY);
  window.localStorage.removeItem(EXPLORE_TRIAL_ID_KEY);
}

export function clearExploreTrialId() {
  setExploreTrialId("");
}

export function isEmbeddedSession() {
  if (typeof window === "undefined") {
    return false;
  }
  return window.sessionStorage.getItem(EMBEDDED_MODE_KEY) === "1";
}

export function getEmbeddedOrgId() {
  if (typeof window === "undefined") {
    return "";
  }
  return String(window.sessionStorage.getItem(EMBEDDED_ORG_ID_KEY) || "").trim();
}

export function setEmbeddedSession({ accessToken, refreshToken = "", orgId }) {
  if (typeof window === "undefined") {
    return;
  }
  const normalizedAccessToken = String(accessToken || "").trim().replace(/^Bearer\s+/i, "").replace(/^["']|["']$/g, "");
  const normalizedRefreshToken = String(refreshToken || "").trim().replace(/^["']|["']$/g, "");
  const normalizedOrgId = String(orgId || "").trim();
  if (!normalizedAccessToken || !normalizedOrgId) {
    return;
  }
  window.sessionStorage.setItem(EMBEDDED_MODE_KEY, "1");
  window.sessionStorage.setItem(EMBEDDED_ORG_ID_KEY, normalizedOrgId);
  setAuthToken(normalizedAccessToken);
  setAuthRefreshToken(normalizedRefreshToken);
}

export function clearEmbeddedSession() {
  if (typeof window === "undefined") {
    return;
  }
  window.sessionStorage.removeItem(EMBEDDED_MODE_KEY);
  window.sessionStorage.removeItem(EMBEDDED_ORG_ID_KEY);
}

export function applyEmbeddedHeaders(config) {
  if (!isEmbeddedSession()) {
    return config;
  }
  const orgId = getEmbeddedOrgId();
  const currentParams = config.params;
  if (currentParams instanceof URLSearchParams) {
    currentParams.set("embedded", "1");
    if (orgId && !currentParams.get("org_id")) {
      currentParams.set("org_id", orgId);
    }
    return config;
  }

  config.params = {
    ...(currentParams || {}),
    embedded: "1",
  };
  if (orgId && !config.params.org_id) {
    config.params.org_id = orgId;
  }
  return config;
}

export function applyExploreTrialHeader(config) {
  const requestUrl = String(config?.url || "");
  if (
    requestUrl.includes("/auth/trial/start") ||
    requestUrl.includes("/auth/trial/invite") ||
    requestUrl.includes("/auth/logout/url") ||
    requestUrl.includes("/auth/oauth/url") ||
    requestUrl.includes("/auth/oauth/exchange") ||
    requestUrl.includes("/auth/methods")
  ) {
    return config;
  }

  const exploreTrialId = getExploreTrialId();
  if (!exploreTrialId) {
    return config;
  }

  config.headers = config.headers || {};
  config.headers["X-Explore-Trial-Id"] = exploreTrialId;
  return config;
}

export function getAuthToken() {
  const storage = getAuthStorage();
  if (!storage) {
    return "";
  }
  return storage.getItem(AUTH_TOKEN_KEY) || "";
}

export function setAuthToken(token) {
  const storage = getAuthStorage();
  if (!storage) {
    return;
  }
  if (token) {
    storage.setItem(AUTH_TOKEN_KEY, token);
  } else {
    storage.removeItem(AUTH_TOKEN_KEY);
  }
  scheduleProactiveRefresh();
}

export function getAuthIdToken() {
  const storage = getAuthStorage();
  if (!storage) {
    return "";
  }
  return storage.getItem(AUTH_ID_TOKEN_KEY) || "";
}

export function setAuthIdToken(token) {
  const storage = getAuthStorage();
  if (!storage) {
    return;
  }
  if (token) {
    storage.setItem(AUTH_ID_TOKEN_KEY, token);
  } else {
    storage.removeItem(AUTH_ID_TOKEN_KEY);
  }
}

export function getAuthRefreshToken() {
  const storage = getAuthStorage();
  if (!storage) {
    return "";
  }
  return storage.getItem(AUTH_REFRESH_TOKEN_KEY) || "";
}

export function setAuthRefreshToken(token) {
  const storage = getAuthStorage();
  if (!storage) {
    return;
  }
  if (token) {
    storage.setItem(AUTH_REFRESH_TOKEN_KEY, token);
  } else {
    storage.removeItem(AUTH_REFRESH_TOKEN_KEY);
  }
  scheduleProactiveRefresh();
}

function clearAuthTokenInternal({ broadcast = false } = {}) {
  const storage = getAuthStorage();
  if (!storage || typeof window === "undefined") {
    return;
  }
  clearProactiveRefreshTimer();
  storage.removeItem(AUTH_TOKEN_KEY);
  storage.removeItem(AUTH_ID_TOKEN_KEY);
  storage.removeItem(AUTH_REFRESH_TOKEN_KEY);
  window.localStorage.removeItem(AUTH_TOKEN_KEY);
  window.localStorage.removeItem(AUTH_ID_TOKEN_KEY);
  window.localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
  window.sessionStorage.removeItem(AUTH_TOKEN_KEY);
  window.sessionStorage.removeItem(AUTH_ID_TOKEN_KEY);
  window.sessionStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
  window.sessionStorage.removeItem(EMBEDDED_MODE_KEY);
  window.sessionStorage.removeItem(EMBEDDED_ORG_ID_KEY);
  if (broadcast) {
    publishAuthSync("logout");
  }
}

export function clearAuthToken() {
  clearAuthTokenInternal({ broadcast: true });
}

export async function refreshAuthSession() {
  const refreshToken = getAuthRefreshToken();
  if (!refreshToken) {
    throw new Error("Missing refresh token");
  }

  const exploreTrialId = getExploreTrialId();
  const { data } = await refreshClient.post("/auth/refresh", {
    refresh_token: refreshToken,
    explore_trial_id: exploreTrialId || undefined,
  });

  const token = data?.access_token || "";
  if (!token) {
    const error = new Error("Refresh did not return access token");
    error.response = { data: { detail: "Refresh failed: token not returned." } };
    throw error;
  }

  setAuthToken(token);
  setAuthIdToken(data?.id_token || "");
  setAuthRefreshToken(data?.refresh_token || refreshToken);
  scheduleProactiveRefresh();
  return data;
}

export async function recoverAuthSession() {
  const accessToken = String(getAuthToken() || "").trim();
  const refreshToken = String(getAuthRefreshToken() || "").trim();

  if (!accessToken && !refreshToken) {
    return false;
  }

  if (!refreshToken) {
    return Boolean(accessToken);
  }

  await refreshAuthSession();
  return true;
}

export function attachRefreshTokenInterceptor(client) {
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      normalizeRequestError(error);
      const status = error?.response?.status;
      const originalConfig = error?.config;
      if (!originalConfig || status !== 401 || originalConfig._retry) {
        throw error;
      }

      const requestUrl = String(originalConfig.url || "");
      if (requestUrl.includes("/auth/refresh")) {
        throw error;
      }

      const refreshToken = getAuthRefreshToken();
      if (!refreshToken) {
        clearAuthToken();
        dispatchAuthExpired({
          reason: "missing_refresh_token",
          status,
          message: "Session expired. Please login again.",
        });
        throw error;
      }

      originalConfig._retry = true;

      try {
        if (!refreshInFlight) {
          refreshInFlight = refreshAuthSession().finally(() => {
            refreshInFlight = null;
          });
        }
        await refreshInFlight;

        originalConfig.headers = originalConfig.headers || {};
        originalConfig.headers.Authorization = `Bearer ${getAuthToken()}`;
        return client(originalConfig);
      } catch (refreshError) {
        clearAuthToken();
        dispatchAuthExpired({
          reason: "refresh_failed",
          status: Number(refreshError?.response?.status || status || 0),
          message:
            refreshError?.response?.data?.detail ||
            refreshError?.response?.data?.message ||
            refreshError?.message ||
            "Session expired. Please login again.",
        });
        throw refreshError;
      }
    }
  );
}

export function subscribeToAuthExpired(handler) {
  if (typeof window === "undefined") {
    return () => {};
  }

  const listener = (event) => {
    handler(event?.detail || {});
  };

  window.addEventListener(AUTH_EXPIRED_EVENT, listener);
  return () => {
    window.removeEventListener(AUTH_EXPIRED_EVENT, listener);
  };
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== AUTH_SYNC_KEY || !event.newValue) {
      return;
    }

    try {
      const payload = JSON.parse(event.newValue);
      if (payload?.action !== "logout") {
        return;
      }

      clearAuthTokenInternal({ broadcast: false });
      dispatchAuthExpired({
        reason: "remote_logout",
        message: "You were logged out in another tab.",
      });
    } catch {
      // Ignore malformed sync payloads.
    }
  });
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return applyEmbeddedHeaders(applyExploreTrialHeader(config));
});

refreshClient.interceptors.request.use((config) => applyEmbeddedHeaders(applyExploreTrialHeader(config)));

attachRefreshTokenInterceptor(apiClient);
attachTransientRetryInterceptor(apiClient);
refreshClient.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(normalizeRequestError(error))
);
apiClient.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(normalizeRequestError(error))
);
migrateLegacySessionTokens();
scheduleProactiveRefresh();

export function buildParams({ customerId, orgId, ...extraParams } = {}) {
  const params = {};
  if (customerId) {
    params.customer_id = customerId;
  }
  const resolvedOrgId = orgId || getEmbeddedOrgId();
  if (resolvedOrgId) {
    params.org_id = resolvedOrgId;
  }
  Object.entries(extraParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params[key] = value;
    }
  });
  return params;
}
